import { type CSSProperties, useEffect, useState } from 'react';
import type { LiveSplash as LiveSplashContent } from '@/types';
import { maskStyle } from './masks';

// `next/font/local` loaded AeonikMono-Bold.otf and put its generated class on the three
// text blocks below; Rails serves the same file through the `Aeonik Mono` family declared
// in stylesheets/fonts.css, so asking for weight 700 selects the identical face.
const AEONIK_BOLD: CSSProperties = {
  fontFamily: "'Aeonik Mono', ui-monospace, monospace",
  fontWeight: 700,
};

type ObsStudioEvent = CustomEvent<{ visible?: boolean; active?: boolean }>;

// The "ya volvemos" / "empezamos pronto" screens OBS shows between blocks. Whenever OBS
// makes the browser source visible again it fires one of its own events, and the whole
// scene is remounted so the entrance animation replays.
export function LiveSplash({
  splash,
  subtitle,
  message,
}: {
  splash: LiveSplashContent;
  subtitle?: string;
  message: string;
}) {
  const [playId, setPlayId] = useState(0);

  useEffect(() => {
    const replay = () => setPlayId((id) => id + 1);

    const onActive = (event: Event) => {
      const { active, visible } = (event as ObsStudioEvent).detail;
      if (active ?? visible) replay();
    };

    window.addEventListener('obsSourceActiveChanged', onActive);
    window.addEventListener('obsSourceVisibleChanged', onActive);

    return () => {
      window.removeEventListener('obsSourceActiveChanged', onActive);
      window.removeEventListener('obsSourceVisibleChanged', onActive);
    };
  }, []);

  return (
    <SplashScene
      key={playId}
      splash={splash}
      subtitle={subtitle}
      message={message}
    />
  );
}

function SplashScene({
  splash,
  subtitle,
  message,
}: {
  splash: LiveSplashContent;
  subtitle?: string;
  message: string;
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <main className="relative h-dvh w-dvw overflow-hidden bg-background">
      <style>{`
        @keyframes live-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(calc(var(--float) * -1px)) rotate(2deg); }
        }
        @keyframes live-text-pulse {
          0%, 100% { color: hsl(var(--primary) / 0.45); }
          50% { color: hsl(var(--primary)); }
        }
      `}</style>

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {splash.items.map((item) => {
          const flip = item.flip ? ' scaleX(-1)' : '';
          return (
            <div
              key={item.id}
              className={`absolute ${item.className}`}
              style={{
                opacity: mounted ? (item.opacity ?? 1) : 0,
                transform: mounted
                  ? `scale(1) rotate(${item.rotate}deg)${flip}`
                  : `scale(0.82) rotate(${item.rotate - 12}deg)${flip}`,
                transition: `opacity 900ms ease-out ${item.delay}ms, transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) ${item.delay}ms`,
              }}
            >
              <div
                style={
                  {
                    '--float': item.float,
                    animation: mounted
                      ? `live-float ${6 + item.float * 0.4}s ease-in-out ${item.delay + 1200}ms infinite`
                      : undefined,
                  } as CSSProperties
                }
              >
                <img
                  src={item.src}
                  alt=""
                  width={item.width}
                  height={item.height}
                  className="h-auto w-full select-none"
                />
              </div>
            </div>
          );
        })}
      </div>

      <div
        className="absolute inset-x-0 top-[6%] z-20 flex justify-center px-8"
        style={{
          ...AEONIK_BOLD,
          opacity: mounted ? 1 : 0,
          transform: `translateY(${mounted ? 0 : -16}px)`,
          transition:
            'opacity 1000ms ease-out 100ms, transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) 100ms',
        }}
      >
        <span className="text-center text-base text-primary uppercase tracking-[0.4em] sm:text-lg md:text-xl">
          {splash.dateLine}
        </span>
      </div>

      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-10 px-8">
        <div className="flex flex-col items-end gap-2">
          <div
            style={{
              opacity: mounted ? 1 : 0,
              transform: `translateY(${mounted ? 0 : -24}px)`,
              transition:
                'opacity 1000ms ease-out 200ms, transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) 200ms',
            }}
          >
            <img
              src="/hack-26/landing/ph26-logo.png"
              alt="Platanus Hack 26"
              width={1153}
              height={248}
              className="h-auto w-[60vw] max-w-[760px] select-none"
            />
          </div>

          <div
            className="flex items-center gap-3 pr-2"
            style={{
              ...AEONIK_BOLD,
              opacity: mounted ? 1 : 0,
              transform: `translateY(${mounted ? 0 : 12}px)`,
              transition:
                'opacity 1000ms ease-out 350ms, transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) 350ms',
            }}
          >
            <span className="text-primary text-xl lowercase sm:text-2xl">
              ft.
            </span>
            <div
              className={splash.ftLogo.className}
              style={maskStyle(splash.ftLogo)}
            />
          </div>
        </div>

        <div
          className="flex flex-col items-center gap-5"
          style={{
            ...AEONIK_BOLD,
            opacity: mounted ? 1 : 0,
            transform: `translateY(${mounted ? 0 : 16}px)`,
            transition:
              'opacity 1000ms ease-out 500ms, transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) 500ms',
          }}
        >
          {subtitle && (
            <span className="bg-primary px-4 py-1.5 text-center text-2xl text-background uppercase tracking-[0.3em] sm:text-3xl md:text-4xl">
              {subtitle}
            </span>
          )}
          <div className="flex items-center gap-4">
            <span
              className="h-3 w-3 animate-pulse rounded-full bg-primary"
              aria-hidden
            />
            <span
              className="text-center text-xl uppercase tracking-[0.4em] sm:text-2xl md:text-3xl"
              style={{ animation: 'live-text-pulse 2s ease-in-out infinite' }}
            >
              {message}
            </span>
          </div>
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-[5%] z-20 flex justify-center">
        <div
          className={splash.groupLogo.className}
          style={maskStyle(splash.groupLogo, {
            opacity: mounted ? 1 : 0,
            transform: `translateY(${mounted ? 0 : 16}px)`,
            transition:
              'opacity 1000ms ease-out 800ms, transform 1200ms cubic-bezier(0.22, 1, 0.36, 1) 800ms',
          })}
        />
      </div>
    </main>
  );
}
